import { Flex } from 'antd';
import React, { useEffect, useState } from 'react';
import { Button, IconType, semanticTheme, Typography } from '@tools/ui/dime';
import FeedbackForm from '../snippets/FeedbackForm';
import { AgentAvatarIcon, AgentDotIcon } from './AgentIcons';
import {
  AiPanelCard,
  AnimatedCard,
  Body,
  Footer,
  Header,
  PanelWrapper
} from './AiPanel.styles';

const CLOSE_ANIMATION_MS = 300;

export { AiPanelCard };

export type AiPanelProps = {
  title: string;
  subtitle?: string;
  introMessage: string;
  isLoading?: boolean;
  isNoData?: boolean;
  updatedAt?: string;
  cards: React.ReactNode[];
  feedbackPrompt?: string;
  onClose: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onFeedback?: (helpful: boolean, message?: string) => Promise<boolean> | boolean;
  hasAnimated?: boolean;
  onAnimationStart?: () => void;
};

const AiPanel: React.FC<AiPanelProps> = ({
  title,
  subtitle,
  introMessage,
  updatedAt,
  cards,
  feedbackPrompt,
  onClose,
  onRefresh,
  isRefreshing = false,
  isLoading = false,
  isNoData = false,
  onFeedback,
  hasAnimated: hasAnimatedProp = false,
  onAnimationStart
}) => {
  const [hasAnimated] = useState(() => hasAnimatedProp);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!hasAnimated && !isLoading) {
      onAnimationStart?.();
    }
  }, [hasAnimated, isLoading]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, CLOSE_ANIMATION_MS);
  };

  return (
    <PanelWrapper $isClosing={isClosing} $skipEnter={hasAnimated}>
      <Header>
        <Flex align="center" gap={semanticTheme.dime.spacing.m}>
          <AgentAvatarIcon />
          <Flex vertical gap={2}>
            <Typography variant="heading3Bold">{title}</Typography>
            {!!subtitle && (
              <Typography
                variant="body2"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {subtitle}
              </Typography>
            )}
          </Flex>
        </Flex>
        <Flex gap={semanticTheme.dime.spacing.s}>
          {!!onRefresh && (
            <Button
              variant="secondary"
              iconOnly={IconType.Refresh}
              onClick={onRefresh}
              loading={isRefreshing}
              disabled={isRefreshing}
            />
          )}
          <Button
            variant="secondary"
            iconOnly={IconType.Close}
            onClick={handleClose}
          />
        </Flex>
      </Header>

      <Body>
        <Flex align="flex-start" gap={semanticTheme.dime.spacing.s}>
          <AgentDotIcon pulsing={isLoading} noData={isNoData} />
          <Flex vertical gap={2}>
            <Typography variant="body1">{introMessage}</Typography>
            {!!updatedAt && !isNoData && (
              <Typography
                variant="body2"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {updatedAt}
              </Typography>
            )}
          </Flex>
        </Flex>

        {cards.map((card, i) => (
          <AnimatedCard
            key={i}
            $delay={i}
            $visible
            $skipAnim={hasAnimated}
          >
            {card}
          </AnimatedCard>
        ))}
      </Body>

      {!!feedbackPrompt && !!onFeedback && (
        <Footer>
          <FeedbackForm onSubmit={onFeedback} prompt={feedbackPrompt} />
        </Footer>
      )}
    </PanelWrapper>
  );
};

export default AiPanel;
